import { spLogTags } from "@eventr/common";
import { ClaimHide, ColoursObj, MainButton } from "@eventr/common-storybook";
import { useMemo } from "react";
import { routes } from "routing";
import { useShallow } from "zustand/react/shallow";
import { FeatureHide } from "../../common/featureHide";
import { RocketCreateDirectChannelBodyType } from "../../data/Rocket";
import { useGlobalRouter } from "../../feat/globalRouter/useGlobalRouter";
import { useRocketChatContext } from "../../feat/rocketchat/rocketChatProvider";
import { bs, useBoundStore } from "../../state/bound.store";
import { useOverlayStore } from "../../state/overlayState/overlay.store";
import { getLiveToken } from "../../state/rocketchatState/rocketchatUi.store";
import { useCheckClaimsWeb } from "../../v2/hooks/useCheckClaimsWeb";

export const DirectChatButton = ({ user_id, user_id2, fullWidth }: RocketCreateDirectChannelBodyType) => {
  const { pushPath } = useGlobalRouter();
  const { createDirectChannel, openChat, userRooms, userSubscriptions, isActivated } = useRocketChatContext();

  const { orgConfig, rocketchannels } = useBoundStore(
    useShallow((s) => ({
      orgConfig: s.orgConfig,
      rocketchannels: s.rocketchannels[s.selectedItinID],
    })),
  );

  const directChat = useMemo(
    () =>
      rocketchannels?.find((c) => c.type === "direct" && c.members?.includes(user_id) && c.members?.includes(user_id2)),
    [rocketchannels, user_id, user_id2],
  );

  const buttonState = useMemo(() => {
    if (!isActivated || orgConfig.disableChat) {
      return "inactive";
    }

    if (directChat) {
      if (userRooms && userSubscriptions) {
        const room = userRooms.find((room) => {
          return room._id === directChat.channel_id;
        });
        const sub = userSubscriptions.find((sub) => {
          return sub.rid === directChat.channel_id;
        });
        if (sub && sub.alert) {
          return "unread";
        } else if (sub || room) {
          return "read";
        } else {
          return undefined;
        }
      } else {
        return "loading";
      }
    } else {
      if (userRooms && userSubscriptions) {
        return "unjoined";
      } else {
        return "loading";
      }
    }
  }, [directChat, isActivated, orgConfig.disableChat, userRooms, userSubscriptions]);

  const createChat = () => {
    useOverlayStore.getState().setLoading(true);

    createDirectChannel({
      user_id: user_id,
      user_id2: user_id2,
    })
      .then(async (c) => {
        if (c.status === "success") {
          await bs().pullRocketChatChannels();
          openChat(c?.data?.channel_id, "d");
        } else {
          spLogTags("Error creating direct chat", ["ERROR", "rocketchat"], c);
        }
      })
      .catch((err) => {
        spLogTags("Exception while creating direct chat", ["ERROR", "rocketchat"], err);
      })
      .finally(() => {
        useOverlayStore.getState().setLoading(false);
      });
  };

  const searchChat = () => {
    bs()
      .getDirectChannel(user_id, user_id2)
      .then((c) => {
        if (c) {
          openChat(c?.channel_id, "d");
        } else {
          createChat();
        }
      })
      .catch((err) => {
        spLogTags("Error fetching direct channel", ["ERROR", "rocketchat"], err);
      });
  };

  const notReady = buttonState === "inactive" || buttonState === "loading" || !buttonState;

  const numberOfMessages = useMemo(
    () => userRooms.find((room) => room._id === directChat?.channel_id)?.msgs ?? 0,
    [directChat?.channel_id, userRooms],
  );

  const noMessages = numberOfMessages === 0;
  const canSendMessage = useCheckClaimsWeb(["/"], ["sendMessage"]);
  const chatUnavailable = noMessages && !canSendMessage;

  return notReady || chatUnavailable ? (
    <div />
  ) : (
    <FeatureHide requiredFeature="Chat">
      <ClaimHide paths={["/"]} claims={["chatAccess"]}>
        <MainButton
          id={"directChatButton" + user_id2 + user_id}
          style={
            !fullWidth
              ? {
                  width: "24px",
                  height: "24px",
                  padding: "0px",
                  margin: "0px",
                  justifyContent: "end",
                  alignSelf: "center",
                  border: "none",
                }
              : { width: "100%" }
          }
          onClick={() => {
            if (getLiveToken() !== undefined) {
              searchChat();
            } else {
              pushPath(routes.chat.list.path);
            }
          }}
          icon={buttonState === "unread" ? "Chat_Notf" : "Chat"}
          variant={fullWidth ? undefined : "ghost"}
          text={fullWidth ? "Message" : undefined}
          iconColour={
            fullWidth ? undefined : buttonState === "unjoined" ? ColoursObj.shades.shade2 : ColoursObj.core.primary
          }
        />
      </ClaimHide>
    </FeatureHide>
  );
};
